// COP Copilot runtime — the turn state machine (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// LIFTED VERBATIM from takbot services/agent-runtime/src/loop.ts (import source changed:
// @takbot/protocol → ./types.ts). In takbot the iframe drove this loop over HTTP; in the COP the
// in-process driver (driver.ts) drives it directly — but the loop's per-(session,turn) state
// machine, anti-loop guard, step cap, and forced-LIMITED finalize are UNCHANGED. This is the
// load-bearing reliability layer: the loop ALWAYS terminates (step cap / backend error / repeat).
//
// A turn:
//   1. handleTurn      { session_id, user_text, manifest }  → Backend.plan(fresh state)
//        → AgentStep{ tool_calls } (or {final} immediately)
//   2. the driver runs each tool_call over the CONFINED working set, collects ToolResults
//   3. handleContinue  { session_id, turn_id, tool_results } → record + Backend.plan(updated)
//        → AgentStep{ tool_calls | final }
//   4. repeat 2–3 until status === "final".

import type {
  AgentContinueRequest,
  AgentStep,
  AgentTurnRequest,
  FinalAnswer,
  ToolCall,
} from "./types.ts";
import type { Backend, Plan, SessionState } from "./backends/types.ts";
import { pairSteps } from "./backends/types.ts";
import { loadContextConfig } from "./config.ts";

/** A monotonic counter folded into turn ids so two turns never collide within a millisecond. */
let TURN_SEQ = 0;

function newTurnId(sessionId: string): string {
  TURN_SEQ += 1;
  return `turn-${sanitize(sessionId)}-${Date.now().toString(36)}-${TURN_SEQ}`;
}

function sanitize(s: string): string {
  return s.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 24) || "anon";
}

/** The in-memory session store: one live SessionState per (session_id, turn_id). */
export class TurnStore {
  private readonly byTurn = new Map<string, SessionState>();

  key(sessionId: string, turnId: string): string {
    return `${sessionId}::${turnId}`;
  }

  put(state: SessionState): void {
    this.byTurn.set(this.key(state.session_id, state.turn_id), state);
  }

  get(sessionId: string, turnId: string): SessionState | undefined {
    return this.byTurn.get(this.key(sessionId, turnId));
  }

  delete(sessionId: string, turnId: string): void {
    this.byTurn.delete(this.key(sessionId, turnId));
  }

  /** Evict any other in-flight turns for a session when a new turn starts. */
  evictSession(sessionId: string): void {
    const prefix = `${sessionId}::`;
    for (const k of this.byTurn.keys()) {
      if (k.startsWith(prefix)) this.byTurn.delete(k);
    }
  }
}

/** The runtime loop driver — owns the store + the active backend. */
export class AgentLoop {
  constructor(
    private readonly backend: Backend,
    private readonly store: TurnStore = new TurnStore(),
  ) {}

  /** Begin a new turn: build fresh state, ask the backend for the first plan. */
  async handleTurn(req: AgentTurnRequest): Promise<AgentStep> {
    if (!req.session_id || typeof req.user_text !== "string" || !req.manifest) {
      throw new BadRequestError("turn requires session_id, user_text, and manifest");
    }
    this.store.evictSession(req.session_id);

    const turnId = newTurnId(req.session_id);
    const state: SessionState = {
      session_id: req.session_id,
      turn_id: turnId,
      userText: req.user_text,
      manifest: req.manifest,
      transcript: [],
      config: loadContextConfig(),
    };
    this.store.put(state);

    const plan = await this.nextPlan(state);
    return this.applyPlan(state, plan);
  }

  /** Continue an in-flight turn: record the tool results, then ask the backend for the next plan. */
  async handleContinue(req: AgentContinueRequest): Promise<AgentStep> {
    if (!req.session_id || !req.turn_id || !Array.isArray(req.tool_results)) {
      throw new BadRequestError("continue requires session_id, turn_id, and tool_results[]");
    }
    const state = this.store.get(req.session_id, req.turn_id);
    if (!state) {
      throw new NotFoundError(`no in-flight turn ${req.turn_id} for session ${req.session_id}`);
    }

    state.transcript.push({ role: "tool", toolResults: req.tool_results });

    const plan = await this.nextPlan(state);
    return this.applyPlan(state, plan);
  }

  /** LOOP SAFETY around the backend: cap model steps, convert any error/timeout into a LIMITED
   *  final, and detect a repeated tool call → finalize instead of looping. */
  private async nextPlan(state: SessionState): Promise<Plan> {
    const modelSteps = state.transcript.filter((e) => e.role === "model").length;
    if (modelSteps >= state.config.max_steps) {
      return this.terminate(
        state,
        `Reached the ${state.config.max_steps}-step limit; answering with what is grounded so far.`,
      );
    }

    let plan: Plan;
    try {
      plan = await this.backend.plan(state);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return forcedFinal(`The model backend failed or timed out (${msg}). This answer is LIMITED.`);
    }

    if (plan.kind === "tool_calls" && plan.tool_calls.length > 0) {
      const alreadyRun = new Set(pairSteps(state.transcript).map((p) => callSig(p.call)));
      const allRepeats = plan.tool_calls.every((c) => alreadyRun.has(callSig(c)));
      if (allRepeats) {
        return this.terminate(
          state,
          "The model repeated a tool call that was already run; answering from the existing results to avoid a loop.",
        );
      }
    }
    return plan;
  }

  /** Terminate by asking the backend to SYNTHESIZE a grounded final from the results already
   *  gathered. Falls back to the canned final only if the finalize call itself fails. */
  private async terminate(state: SessionState, reason: string): Promise<Plan> {
    try {
      const final = await this.backend.finalize(state);
      return { kind: "final", final };
    } catch {
      return forcedFinal(reason);
    }
  }

  /** Turn a backend Plan into an AgentStep, recording issued tool calls + evicting on final. */
  private applyPlan(state: SessionState, plan: Plan): AgentStep {
    if (plan.kind === "tool_calls") {
      const toolCalls = plan.tool_calls;
      state.transcript.push({ role: "model", toolCalls });
      return {
        turn_id: state.turn_id,
        status: "tool_calls",
        tool_calls: toolCalls,
        trace: traceForCalls(this.backend, toolCalls),
      };
    }
    const final: FinalAnswer = plan.final;
    this.store.delete(state.session_id, state.turn_id);
    return {
      turn_id: state.turn_id,
      status: "final",
      final,
      trace: `[${this.backend.name}/${this.backend.model}] final${final.marking ? ` @ ${final.marking}` : ""}`,
    };
  }
}

function traceForCalls(backend: Backend, calls: ToolCall[]): string {
  const ids = calls.map((c) => c.capability).join(", ");
  return `[${backend.name}/${backend.model}] tool_calls: ${ids}`;
}

/** Stable signature of a tool call (capability + args) for the anti-loop guard. */
function callSig(call: ToolCall): string {
  return `${call.capability}::${stableStringify(call.args)}`;
}

function stableStringify(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v) ?? "null";
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`;
  const rec = v as Record<string, unknown>;
  const keys = Object.keys(rec).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(rec[k])}`).join(",")}}`;
}

/** A loop-safety final: a terminating, honestly-LIMITED answer the runtime injects when it must
 *  stop the loop itself (step cap, backend error, or a detected repeat). */
function forcedFinal(text: string): Plan {
  return {
    kind: "final",
    final: {
      text,
      citations: [],
      coverage_warning:
        "The runtime ended the loop for safety (step limit, backend error, or a repeated call). Treat this as a non-answer, not a definitive negative.",
    },
  };
}

/** A 400-class loop error (malformed request). */
export class BadRequestError extends Error {
  readonly httpStatus = 400;
}
/** A 404-class loop error (unknown turn). */
export class NotFoundError extends Error {
  readonly httpStatus = 404;
}
